// Omnix/client/ui/ChannelManager.js
import { ComponentBase } from "../core/ComponentBase.js";

export class ChannelManager extends ComponentBase {
    static styles = `
        .channel-manager-container {
            padding: 1rem 1.5rem;
            display: flex;
            flex-direction: column;
            height: 100%; /* Take full available height */
        }

        #channel-grid-container {
            flex-grow: 1; /* Allow this container to take up remaining space */
            overflow-y: auto; /* Add vertical scrollbar when needed */
            padding-bottom: 1rem; /* Add some space at the bottom */
        }

        .channel-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
            gap: 1.5rem;
        }
        .channel-card {
            border: 1px solid var(--bs-border-color);
            border-left-width: 4px;
            transition: all 0.2s ease-in-out;
        }
        .channel-card:hover {
            transform: translateY(-3px);
            box-shadow: var(--bs-box-shadow);
        }
        .channel-card-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }
        .channel-icon {
            font-size: 1.75rem;
            width: 40px;
            height: 40px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: var(--bs-border-radius);
            background-color: var(--bs-tertiary-bg);
        }
        .channel-name {
            font-weight: 500;
            font-size: 1.1rem;
            margin-bottom: 0;
        }
        .channel-identifier {
            font-size: 0.8rem;
            color: var(--bs-secondary-color);
            font-family: monospace;
            word-break: break-all;
        }
        .channel-workflow {
            font-size: 0.85rem;
            font-style: italic;
            color: var(--bs-secondary-color);
        }
        .channel-workflow .badge {
            font-style: normal;
        }
        .channel-card-footer {
            background-color: var(--bs-tertiary-bg);
        }
    `;

    constructor(config) {
        super(config);
        this.configs = [];
        this.wizards = {};
        this.workflowSelectorModal = null;
        this._boundHandleChannelSaved = this.handleChannelSaved.bind(this);
        this.applyStyles();
    }

    async initialize() {
        // @bundle-include: EmailWizard
        // @bundle-include: WebChatWizard
        // @bundle-include: TwilioWizard
        // @bundle-include: MetaWebhookWizard
        // @bundle-include: TelegramWizard
        // @bundle-include: WorkflowSelectorModal
        
        const wizardTypes = [
            { name: 'Email', wizard: 'EmailWizard', icon: 'bi-envelope-fill' },
            { name: 'Web Chat', wizard: 'WebChatWizard', icon: 'bi-chat-dots-fill' },
            { name: 'Twilio (SMS/Voice)', wizard: 'TwilioWizard', icon: 'bi-telephone-fill' },
            { name: 'WhatsApp', wizard: 'MetaWebhookWizard', provider: 'whatsapp_business_api', channelType: 'whatsapp', icon: 'bi-whatsapp' },
            { name: 'Facebook', wizard: 'MetaWebhookWizard', provider: 'facebook_messenger', channelType: 'facebook_messenger', icon: 'bi-messenger' },
            { name: 'Instagram', wizard: 'MetaWebhookWizard', provider: 'instagram', channelType: 'instagram', icon: 'bi-instagram' },
            { name: 'Telegram', wizard: 'TelegramWizard', icon: 'bi-telegram' }
        ];

        for (const type of wizardTypes) {
            this.wizards[type.name] = await this.createManagedComponent(type.wizard, null, {
                instanceId: `${this.config.instanceId}-${type.name.toLowerCase()}-wizard`,
                providerName: type.provider, // For MetaWebhookWizard
                channelType: type.channelType // For MetaWebhookWizard
            });
            this.wizards[type.name].on('channelSaved', this._boundHandleChannelSaved);
        }

        this.workflowSelectorModal = await this.createManagedComponent('WorkflowSelectorModal', null, {
            instanceId: `${this.config.instanceId}-workflow-selector`
        });
        this.workflowSelectorModal.on('workflowSelected', this.handleWorkflowSelected.bind(this));

        await super.initialize();
    }

    async initAfterLogin() {
        await this.loadConfigs();
    }

    handleChannelSaved() {
        this.loadConfigs();
    }

    async loadConfigs() {
        try {
            const result = await this.appClient.rpcClient.call('ChannelManagerRpc.getData', { pageSize: 1000 });
            this.configs = result.data || [];
            if (this.container) this.render();
        } catch (error) {
            this.log.error("Failed to load channel configs:", error);
        }
    }

    async handleWorkflowSelected({ channelId, workflowIds }) {
        try {
            await this.appClient.rpcClient.call('ChannelManagerRpc.update', {
                _id: channelId,
                ...workflowIds
            });
            this.appClient.notifier.success("Workflow updated successfully.");
            await this.loadConfigs();
        } catch (error) {
            this.log.error("Failed to update workflow:", error);
            this.appClient.notifier.error("Could not update workflow.");
        }
    }


    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'channel-manager-container';
        }

        const addChannelButtons = Object.entries(this.wizards).map(([name, wizardInstance]) => {
            const wizardInfo = [
                { name: 'Email', icon: 'bi-envelope-fill' },
                { name: 'Web Chat', icon: 'bi-chat-dots-fill' },
                { name: 'Twilio (SMS/Voice)', icon: 'bi-telephone-fill' },
                { name: 'WhatsApp', icon: 'bi-whatsapp' },
                { name: 'Facebook', icon: 'bi-messenger' },
                { name: 'Instagram', icon: 'bi-instagram' },
                { name: 'Telegram', icon: 'bi-telegram' }
            ].find(w => w.name === name);
            return `<li><a class="dropdown-item" href="#" data-wizard="${name}"><i class="bi ${wizardInfo.icon} me-2"></i>${this.t(`Add ${name}`)}</a></li>`;
        }).join('');

        this.container.innerHTML = `
            <div class="d-flex justify-content-between align-items-center mb-4 flex-shrink-0">
                <div>
                    <h3 data-i18n="Channel Management">Channel Management</h3>
                    <p class="text-muted mb-0" data-i18n="Configure and manage all your communication channels and providers.">Configure and manage all your communication channels and providers.</p>
                </div>
                <div class="btn-group">
                    <button class="btn btn-primary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                        <i class="bi bi-plus-lg"></i> ${this.t('Add Channel')}
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">${addChannelButtons}</ul>
                </div>
            </div>
            <div class="channel-grid" id="channel-grid-container"></div>
        `;


        const gridContainer = this.container.querySelector('#channel-grid-container');
        if (this.configs.length === 0) {
            gridContainer.innerHTML = `<div class="alert alert-info">${this.t('No channels have been configured yet.')}</div>`;
        } else {
            this.configs.forEach(config => {
                const card = this.createChannelCard(config);
                gridContainer.appendChild(card);
            });
        }

        this.container.querySelector('.dropdown-menu').addEventListener('click', (e) => {
            const wizardName = e.target.closest('a')?.dataset.wizard;
            if (wizardName && this.wizards[wizardName]) {
                this.wizards[wizardName].show('create');
            }
        });

        return this.container;
    }

    createChannelCard(config) {
        const card = document.createElement('div');
        const channelIcons = {
            email: { icon: 'bi-envelope-fill', color: 'text-danger' },
            sms: { icon: 'bi-chat-dots-fill', color: 'text-success' },
            whatsapp: { icon: 'bi-whatsapp', color: 'text-success' },
            web_chat: { icon: 'bi-globe', color: 'text-primary' },
            facebook_messenger: { icon: 'bi-messenger', color: 'text-primary' },
            instagram: { icon: 'bi-instagram', color: 'text-warning' },
            telegram: { icon: 'bi-telegram', color: 'text-info' },
            voice: { icon: 'bi-telephone-fill', color: 'text-secondary' }
        };
        const info = channelIcons[config.channelType] || { icon: 'bi-hdd-stack-fill', color: 'text-muted' };
        const borderColor = getComputedStyle(document.documentElement).getPropertyValue(`--bs-${info.color.replace('text-', '')}`).trim();

        card.className = 'card channel-card';
        card.style.borderLeftColor = borderColor;

        let identifierHtml = '';
        if (config.provider === 'twilio') {
            identifierHtml = `<div class="channel-identifier">${config.settings?.phoneNumber || 'N/A'}</div>`;
        } else if (config.provider === 'native' && config.channelType === 'web_chat') {
            identifierHtml = `<div class="channel-identifier">${config.settings?.widgetId || 'N/A'}</div>`;
        } else if (config.provider === 'smtp') {
            identifierHtml = `<div class="channel-identifier">${config.settings?.smtpUser || 'N/A'}</div>`;
        }

        let workflowHtml = '';
        if (config.provider === 'twilio') {
            const smsWorkflowName = config.inboundSmsWorkflowIdDisplay || 'Inbox';
            const voiceWorkflowName = config.inboundVoiceWorkflowIdDisplay || 'Inbox';
            workflowHtml = `
                <div class="channel-workflow mt-2">
                    <i class="bi bi-chat-dots-fill me-1"></i> SMS: <span class="badge bg-light text-dark">${smsWorkflowName}</span>
                </div>
                <div class="channel-workflow">
                    <i class="bi bi-telephone-fill me-1"></i> Voice: <span class="badge bg-light text-dark">${voiceWorkflowName}</span>
                </div>`;
        } else {
            const workflowName = config.inboundWorkflowIdDisplay || 'Inbox';
            workflowHtml = `
                <div class="channel-workflow mt-2">
                    <i class="bi bi-arrow-down-left-circle me-1"></i> Inbound: <span class="badge bg-light text-dark">${workflowName}</span>
                </div>`;
        }


        card.innerHTML = `
            <div class="card-body">
                <div class="channel-card-header">
                    <div class="channel-icon ${info.color}"><i class="bi ${info.icon}"></i></div>
                    <div>
                        <h5 class="channel-name">${config.name}</h5>
                        ${identifierHtml}
                    </div>
                </div>
                ${workflowHtml}
            </div>
            <div class="card-footer d-flex justify-content-between align-items-center">
                <span class="badge bg-${config.status === 'connected' ? 'success' : 'warning'} text-dark">${config.status}</span>
                <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-secondary" data-action="set-workflow" title="Set Workflow"><i class="bi bi-diagram-3"></i></button>
                    <button class="btn btn-outline-danger" data-action="delete" title="Delete"><i class="bi bi-trash"></i></button>
                </div>
            </div>
        `;

        card.querySelector('[data-action="set-workflow"]').addEventListener('click', () => {
            this.workflowSelectorModal.show(config);
        });
        card.querySelector('[data-action="delete"]').addEventListener('click', () => this.handleDelete(config._id, config.name));

        return card;
    }

    async handleDelete(configId, configName) {
        const confirmed = await this.appClient.notifier.confirm(`Are you sure you want to delete the channel "${configName}"?`, { type: 'danger' });
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('ChannelManagerRpc.delete', { _id: configId });
                this.appClient.notifier.success("Channel deleted successfully.");
                await this.loadConfigs(); // Refresh the list
            } catch (error) {
                this.log.error(`Failed to delete channel ${configId}:`, error);
                this.appClient.notifier.error(`Delete failed: ${error.message}`);
            }
        }
    }

    destroy() {
        Object.values(this.wizards).forEach(wizard => {
            wizard?.off('channelSaved', this._boundHandleChannelSaved);
        });
        this.workflowSelectorModal?.off('workflowSelected', this.handleWorkflowSelected);
        super.destroy();
    }
}